/**
 * SEO Routes — All programmatic SEO routes.
 * Added as an extension to existing routes. Does NOT modify existing routes.
 * Levels 1-5: city, brand×city, category×city, airport, multilanguage.
 * Phase 2 (Levels 6-8): blog network, FAQ network, guides, lists, comparisons.
 */

import { lazy } from 'react';
import { Route } from 'react-router-dom';

const FrLegacyCityRedirect = lazy(() => import('@/routes/FrLegacyCityRedirect'));
const SEOLandingPage = lazy(() => import('@/pages/seo/SEOLandingPage'));
const SEOIndexPage = lazy(() => import('@/pages/seo/SEOIndexPage'));
const SEOBlogPage = lazy(() => import('@/pages/seo/SEOBlogPage'));
const SEOFaqPage = lazy(() => import('@/pages/seo/SEOFaqPage'));
const SEOBlogPageV2 = lazy(() => import('@/pages/seo/SEOBlogPageV2'));
const SEOFaqPageV2 = lazy(() => import('@/pages/seo/SEOFaqPageV2'));
const SEOGuidePage = lazy(() => import('@/pages/seo/SEOGuidePage'));
const SEOListPage = lazy(() => import('@/pages/seo/SEOListPage'));
const SEOComparePage = lazy(() => import('@/pages/seo/SEOComparePage'));
const SEOAboutPage = lazy(() => import('@/pages/seo/SEOAboutPage'));
const SEOContactPage = lazy(() => import('@/pages/seo/SEOContactPage'));

export const SEORoutes = () => (
  <>
    {/* ─── Entity pages (sitemap Tier-1) ───────────────────────────────── */}
    <Route path="/about-rakb" element={<SEOAboutPage lang="fr" />} />
    <Route path="/a-propos-rakb" element={<SEOAboutPage lang="fr" />} />
    <Route path="/contact-rakb" element={<SEOContactPage lang="fr" />} />

    {/* ─── FR Routes (L1-5) ────────────────────────────────────────────── */}

    <Route path="/fr/location-voiture" element={<SEOIndexPage lang="fr" />} />
    <Route path="/en/car-rental" element={<SEOIndexPage lang="en" />} />
    <Route path="/ar/location-voiture" element={<SEOIndexPage lang="ar" />} />

    <Route path="/fr/location-voiture/:city" element={<FrLegacyCityRedirect />} />
    <Route path="/fr/location-voiture/:brand/:city" element={<SEOLandingPage pageType="brand-city" />} />
    <Route path="/fr/location-voiture/aeroport/:city" element={<SEOLandingPage pageType="airport-city" />} />
    <Route path="/fr/location-voiture/categorie/:category" element={<SEOLandingPage pageType="category" />} />
    <Route path="/fr/location-voiture/longue-duree" element={<SEOLandingPage pageType="long-term" />} />
    <Route path="/fr/location-voiture/pas-cher" element={<SEOLandingPage pageType="cheap" />} />
    <Route path="/fr/location-voiture/luxe" element={<SEOLandingPage pageType="luxury" />} />

    {/* ─── EN Routes (L1-5) ────────────────────────────────────────────── */}

    <Route path="/en/car-rental/:city" element={<SEOLandingPage pageType="city" />} />
    <Route path="/en/car-rental/:brand/:city" element={<SEOLandingPage pageType="brand-city" />} />
    <Route path="/en/car-rental/airport/:city" element={<SEOLandingPage pageType="airport-city" />} />
    <Route path="/en/car-rental/category/:category" element={<SEOLandingPage pageType="category" />} />
    <Route path="/en/car-rental/long-term" element={<SEOLandingPage pageType="long-term" />} />
    <Route path="/en/car-rental/cheap" element={<SEOLandingPage pageType="cheap" />} />
    <Route path="/en/car-rental/luxury" element={<SEOLandingPage pageType="luxury" />} />

    {/* ─── AR Routes (L1-5) ────────────────────────────────────────────── */}

    <Route path="/ar/location-voiture/:city" element={<SEOLandingPage pageType="city" />} />
    <Route path="/ar/location-voiture/:brand/:city" element={<SEOLandingPage pageType="brand-city" />} />
    <Route path="/ar/location-voiture/aeroport/:city" element={<SEOLandingPage pageType="airport-city" />} />
    <Route path="/ar/location-voiture/categorie/:category" element={<SEOLandingPage pageType="category" />} />
    <Route path="/ar/location-voiture/longue-duree" element={<SEOLandingPage pageType="long-term" />} />
    <Route path="/ar/location-voiture/pas-cher" element={<SEOLandingPage pageType="cheap" />} />
    <Route path="/ar/location-voiture/luxe" element={<SEOLandingPage pageType="luxury" />} />

    {/* ─── Blog (L1 legacy) ─────────────────────────────────────────── */}
    <Route path="/blog/location-voiture-maroc" element={<SEOBlogPage slug="location-voiture-maroc" />} />
    <Route path="/blog/location-voiture-casablanca-prix" element={<SEOBlogPage slug="location-voiture-casablanca-prix" />} />
    <Route path="/blog/location-voiture-marrakech-guide" element={<SEOBlogPage slug="location-voiture-marrakech-guide" />} />
    <Route path="/blog/location-voiture-aeroport-maroc" element={<SEOBlogPage slug="location-voiture-aeroport-maroc" />} />
    <Route path="/blog/louer-voiture-maroc-conseils" element={<SEOBlogPage slug="louer-voiture-maroc-conseils" />} />
    <Route path="/blog/location-longue-duree-maroc" element={<SEOBlogPage slug="location-longue-duree-maroc" />} />

    <Route path="/faq/location-voiture" element={<SEOFaqPage />} />

    {/* ─── Phase 2: Blog Network (L6) ──────────────────────────────────── */}
    <Route path="/blog/location-voiture-casablanca" element={<SEOBlogPageV2 lang="fr" />} />
    <Route path="/blog/location-voiture-marrakech" element={<SEOBlogPageV2 lang="fr" />} />
    <Route path="/blog/location-voiture-pas-cher" element={<SEOBlogPageV2 lang="fr" />} />
    <Route path="/blog/location-voiture-longue-duree" element={<SEOBlogPageV2 lang="fr" />} />
    <Route path="/blog/location-voiture-aeroport" element={<SEOBlogPageV2 lang="fr" />} />
    <Route path="/blog/meilleure-location-voiture-maroc" element={<SEOBlogPageV2 lang="fr" />} />
    <Route path="/blog/prix-location-voiture-maroc" element={<SEOBlogPageV2 lang="fr" />} />
    <Route path="/blog/location-voiture-tourisme-maroc" element={<SEOBlogPageV2 lang="fr" />} />
    <Route path="/blog/location-voiture-sans-caution-maroc" element={<SEOBlogPageV2 lang="fr" />} />
    <Route path="/blog/location-voiture-electrique-maroc" element={<SEOBlogPageV2 lang="fr" />} />
    {/* EN blog */}
    <Route path="/blog/car-rental-casablanca" element={<SEOBlogPageV2 lang="en" />} />
    <Route path="/blog/car-rental-marrakech" element={<SEOBlogPageV2 lang="en" />} />
    <Route path="/blog/cheap-car-rental-morocco" element={<SEOBlogPageV2 lang="en" />} />
    <Route path="/blog/long-term-car-rental-morocco" element={<SEOBlogPageV2 lang="en" />} />
    <Route path="/blog/airport-car-rental-morocco" element={<SEOBlogPageV2 lang="en" />} />
    <Route path="/blog/best-car-rental-morocco" element={<SEOBlogPageV2 lang="en" />} />
    <Route path="/blog/car-rental-prices-morocco" element={<SEOBlogPageV2 lang="en" />} />
    <Route path="/blog/car-rental-tourism-morocco" element={<SEOBlogPageV2 lang="en" />} />
    <Route path="/blog/no-deposit-car-rental-morocco" element={<SEOBlogPageV2 lang="en" />} />
    <Route path="/blog/electric-car-rental-morocco" element={<SEOBlogPageV2 lang="en" />} />
    {/* Dynamic fallback */}
    <Route path="/blog/:slug" element={<SEOBlogPageV2 />} />

    {/* ─── Phase 2: FAQ Network (L6) ───────────────────────────────────── */}
    <Route path="/faq/location-voiture-maroc" element={<SEOFaqPageV2 lang="fr" />} />
    <Route path="/faq/location-voiture-casablanca" element={<SEOFaqPageV2 lang="fr" />} />
    <Route path="/faq/location-voiture-prix" element={<SEOFaqPageV2 lang="fr" />} />
    <Route path="/faq/location-voiture-assurance" element={<SEOFaqPageV2 lang="fr" />} />
    <Route path="/faq/location-voiture-aeroport" element={<SEOFaqPageV2 lang="fr" />} />
    {/* EN FAQ */}
    <Route path="/faq/car-rental-morocco" element={<SEOFaqPageV2 lang="en" />} />
    <Route path="/faq/car-rental-casablanca-faq" element={<SEOFaqPageV2 lang="en" />} />
    <Route path="/faq/car-rental-prices-faq" element={<SEOFaqPageV2 lang="en" />} />
    <Route path="/faq/car-rental-insurance-faq" element={<SEOFaqPageV2 lang="en" />} />
    <Route path="/faq/airport-car-rental-faq" element={<SEOFaqPageV2 lang="en" />} />
    {/* Dynamic fallback */}
    <Route path="/faq/:slug" element={<SEOFaqPageV2 />} />

    {/* ─── Phase 2: Guide Pages (L7) ───────────────────────────────────── */}
    <Route path="/guide/location-voiture-maroc" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/conduire-au-maroc" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/location-voiture-touriste" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/meilleures-voitures-maroc" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/location-voiture-aeroport-maroc" element={<SEOGuidePage lang="fr" />} />
    {/* EN guides */}
    <Route path="/guide/car-rental-morocco-guide" element={<SEOGuidePage lang="en" />} />
    <Route path="/guide/driving-in-morocco" element={<SEOGuidePage lang="en" />} />
    <Route path="/guide/car-rental-tourist-morocco" element={<SEOGuidePage lang="en" />} />
    <Route path="/guide/best-cars-rent-morocco" element={<SEOGuidePage lang="en" />} />
    <Route path="/guide/airport-car-rental-guide-morocco" element={<SEOGuidePage lang="en" />} />
    {/* Europe → Morocco travel intent (rental used in Morocco only) */}
    <Route path="/guide/location-voiture-maroc-depuis-france" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/rent-car-morocco-from-france" element={<SEOGuidePage lang="en" />} />
    <Route path="/guide/location-voiture-maroc-depuis-belgique" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/rent-car-morocco-from-belgium" element={<SEOGuidePage lang="en" />} />
    <Route path="/guide/location-voiture-maroc-depuis-pays-bas" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/rent-car-morocco-from-netherlands" element={<SEOGuidePage lang="en" />} />
    <Route path="/guide/location-voiture-maroc-depuis-allemagne" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/rent-car-morocco-from-germany" element={<SEOGuidePage lang="en" />} />
    <Route path="/guide/location-voiture-maroc-depuis-espagne" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/rent-car-morocco-from-spain" element={<SEOGuidePage lang="en" />} />
    <Route path="/guide/location-voiture-maroc-depuis-royaume-uni" element={<SEOGuidePage lang="fr" />} />
    <Route path="/guide/rent-car-morocco-from-uk" element={<SEOGuidePage lang="en" />} />
    {/* Dynamic fallback */}
    <Route path="/guide/:slug" element={<SEOGuidePage />} />

    {/* ─── Phase 2: List Pages (L8 AI SEO) ─────────────────────────────── */}
    <Route path="/best-car-rental-morocco" element={<SEOListPage lang="fr" />} />
    <Route path="/best-car-rental-casablanca" element={<SEOListPage lang="fr" />} />
    <Route path="/top-suv-rental-morocco" element={<SEOListPage lang="fr" />} />
    <Route path="/top-cheap-car-rental" element={<SEOListPage lang="fr" />} />

    {/* ─── Phase 2: Comparison Pages (L6) ──────────────────────────────── */}
    <Route path="/compare/rakb-vs-agency" element={<SEOComparePage lang="fr" />} />
    <Route path="/compare/rakb-vs-rental-company" element={<SEOComparePage lang="fr" />} />
    <Route path="/compare/location-vs-taxi" element={<SEOComparePage lang="fr" />} />
    <Route path="/compare/location-vs-uber" element={<SEOComparePage lang="fr" />} />
    {/* EN comparisons */}
    <Route path="/compare/rakb-vs-agency-en" element={<SEOComparePage lang="en" />} />
    <Route path="/compare/rental-vs-taxi" element={<SEOComparePage lang="en" />} />
    <Route path="/compare/rakb-vs-rental-company-en" element={<SEOComparePage lang="en" />} />
    <Route path="/compare/rental-vs-uber" element={<SEOComparePage lang="en" />} />
    {/* Dynamic fallback */}
    <Route path="/compare/:slug" element={<SEOComparePage />} />
  </>
);
